import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { SERVER } from '../../../../config/config';
import { usePermission } from '../../../../hooks/usePermission';
import { 
  FaSave, FaEdit, FaTimes, FaGlobe, FaEnvelope, FaPhone, 
  FaMapMarkerAlt, FaFacebook, FaInstagram, FaLinkedin, FaTwitter, FaTiktok
} from 'react-icons/fa';

const Settings = () => {
  const { can } = usePermission();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  const canEdit = can('content.edit');

  const settingsGroups = [
    {
      key: 'general',
      title: 'General Settings',
      icon: FaGlobe,
      settings: [
        { key: 'site_name', label: 'Site Name', type: 'text', placeholder: 'Your Site Name' },
        { key: 'site_tagline', label: 'Site Tagline', type: 'text', placeholder: 'Your site description' },
        { key: 'company_description', label: 'Company Description', type: 'textarea', placeholder: 'Brief description of your company' }
      ]
    },
    {
      key: 'contact',
      title: 'Contact Information',
      icon: FaEnvelope,
      settings: [
        { key: 'contact_email', label: 'Contact Email', type: 'email', placeholder: 'contact@example.com' },
        { key: 'contact_phone', label: 'Contact Phone', type: 'tel', placeholder: '+1 234 567 8900' },
        { key: 'contact_address', label: 'Address', type: 'textarea', placeholder: 'Your business address' }
      ]
    },
    {
      key: 'social',
      title: 'Social Media Links',
      icon: FaFacebook,
      settings: [
        { key: 'facebook_url', label: 'Facebook URL', type: 'url', placeholder: 'https://facebook.com/yourpage', icon: FaFacebook },
        { key: 'instagram_url', label: 'Instagram URL', type: 'url', placeholder: 'https://instagram.com/yourpage', icon: FaInstagram },
        { key: 'linkedin_url', label: 'LinkedIn URL', type: 'url', placeholder: 'https://linkedin.com/company/yourcompany', icon: FaLinkedin },
        { key: 'twitter_url', label: 'Twitter URL', type: 'url', placeholder: 'https://twitter.com/yourhandle', icon: FaTwitter },
        { key: 'tiktok_url', label: 'TikTok URL', type: 'url', placeholder: 'https://tiktok.com/@yourhandle', icon: FaTiktok }
      ]
    }
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${SERVER}/settings/admin/list`, {
        withCredentials: true
      });

      // Convert array of settings to object grouped by key
      const settingsObj = {};
      response.data.settings.forEach(setting => {
        settingsObj[setting.key] = setting.value;
      });
      
      setSettings(settingsObj);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGroup = async (groupKey) => {
    if (!canEdit) return;
    
    try {
      setSaving(true);
      const groupSettings = settingsGroups.find(g => g.key === groupKey);
      const settingsToSave = {};
      
      groupSettings.settings.forEach(setting => {
        settingsToSave[setting.key] = settings[setting.key] || '';
      });

      await axios.put(`${SERVER}/settings/admin/bulk`, {
        settings: settingsToSave,
        group: groupKey
      }, {
        withCredentials: true
      });

      setEditingGroup(null);
      // Show success message
    } catch (error) {
      console.error('Error saving settings:', error);
      // Show error message
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const renderSettingInput = (setting) => {
    const value = settings[setting.key] || '';
    const isEditing = editingGroup === settingsGroups.find(g => 
      g.settings.some(s => s.key === setting.key)
    )?.key;

    if (!isEditing) {
      return (
        <div className="text-slate-600">
          {value || <span className="text-slate-400 italic">Not set</span>}
        </div>
      );
    }

    switch (setting.type) {
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleInputChange(setting.key, e.target.value)}
            placeholder={setting.placeholder}
            rows={3}
            className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        );
      default:
        return (
          <input
            type={setting.type}
            value={value}
            onChange={(e) => handleInputChange(setting.key, e.target.value)}
            placeholder={setting.placeholder}
            className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-800">Site Settings</h2>
        <p className="text-slate-600">Manage your website's global settings and configuration</p>
      </div>

      {/* Settings Groups */}
      <div className="space-y-6">
        {settingsGroups.map((group) => {
          const GroupIcon = group.icon;
          const isEditing = editingGroup === group.key;
          
          return (
            <div key={group.key} className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
              {/* Group Header */}
              <div className="p-6 border-b border-slate-200 bg-slate-50/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                      <GroupIcon className="text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800">{group.title}</h3>
                      <p className="text-sm text-slate-500">
                        {group.settings.length} setting{group.settings.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {isEditing ? (
                      <>
                        <button
                          onClick={() => setEditingGroup(null)}
                          disabled={saving}
                          className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
                        >
                          <FaTimes />
                          <span>Cancel</span>
                        </button>
                        <button
                          onClick={() => handleSaveGroup(group.key)}
                          disabled={saving || !canEdit}
                          className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <FaSave />
                          <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setEditingGroup(group.key)}
                        disabled={!canEdit}
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <FaEdit />
                        <span>Edit</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Group Settings */}
              <div className="p-6">
                <div className="space-y-6">
                  {group.settings.map((setting) => {
                    const SettingIcon = setting.icon;
                    
                    return (
                      <div key={setting.key} className="flex items-start gap-4">
                        {SettingIcon && (
                          <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center mt-1 flex-shrink-0">
                            <SettingIcon className="text-slate-500 text-sm" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-semibold text-slate-700">
                              {setting.label}
                            </label>
                            {!isEditing && settings[setting.key] && (
                              <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                                Configured
                              </span>
                            )}
                          </div>
                          {renderSettingInput(setting)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
        <div className="flex items-start gap-3">
          <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
            <span className="text-blue-600 text-sm font-bold">?</span>
          </div>
          <div>
            <h4 className="font-semibold text-blue-900 mb-2">Settings Help</h4>
            <div className="text-blue-800 text-sm space-y-1">
              <p>• <strong>General Settings:</strong> Configure your site's basic information and branding</p>
              <p>• <strong>Contact Information:</strong> Set up how visitors can reach you</p>
              <p>• <strong>Social Media:</strong> Add links to your social media profiles</p>
              <p>• Changes are saved immediately when you click "Save Changes"</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;